import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { formatDisplay } from "@/lib/datetime-format";
import { METRIC_LABELS } from "@/types/applied-scenario.types";
import type { AwaitingClassificationItem } from "@/types/closed-loop.types";
import { CAUSE_LABELS } from "./labels";
import { useConfirmClassification } from "./hooks";

interface AwaitingClassificationListProps {
  items: readonly AwaitingClassificationItem[];
}

// Spec 077 (US2 / FR-009, FR-039) — a fila do que foi apurado e ninguém julgou.
//
// Cada linha responde, sem abrir nada: qual linha, que janela, o que mais desviou, e o que a regra
// propõe. Quando a proposta serve, confirmar é UM clique aqui mesmo — a fila é onde o trabalho
// acontece, e obrigar a abrir o compromisso para concordar transformaria uma decisão de segundos
// numa navegação de três telas.
//
// ⚠️ Item com `needsDetail` NÃO ganha botão de confirmar. A fila não consulta interferência externa
// (R12), então a regra não chegou ao fim — oferecer "confirmar" ali seria pedir um julgamento sobre
// uma proposta que não existe.

/** O desvio da métrica que mais se afastou do previsto. `—` quando nenhuma era comparável. */
function deviationText(item: AwaitingClassificationItem): string {
  const top = item.topDeviationMetric;
  if (!top || top.deltaPct === null) return "sem métrica comparável";
  const label = METRIC_LABELS[top.metric] ?? top.metric;
  return `${label} desviou ${top.deltaPct.toFixed(1).replace(".", ",")}%`;
}

export function AwaitingClassificationList({ items }: AwaitingClassificationListProps) {
  const navigate = useNavigate();
  const confirm = useConfirmClassification();

  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const proposed = item.proposal.cause;
        const isPending =
          confirm.isPending && confirm.variables?.appliedScenarioId === item.appliedScenarioId;
        // FR-044 — leva ao COMPROMISSO, não à lista da linha: `?compromisso=` abre o card certo
        // já com a apuração à vista. Sem o id, quem clica tem de adivinhar a janela entre cards
        // colapsados iguais — e o formulário de classificação vive DENTRO do card expandido.
        const open = () =>
          navigate(`/lines/${item.lineId}/applied-scenarios?compromisso=${item.appliedScenarioId}`);

        return (
          <li
            key={item.appliedScenarioId}
            className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border-subtle p-3"
          >
            <div className="min-w-0 space-y-1">
              <p className="text-[13px] font-medium text-text-primary">{item.lineName}</p>
              <p className="text-[12px] text-text-secondary">
                {formatDisplay(item.windowStart)} → {formatDisplay(item.windowEnd)} ·{" "}
                {deviationText(item)}
              </p>
              {proposed ? (
                <p className="text-[12px] text-text-secondary">
                  A regra propõe:{" "}
                  <span className="text-text-primary">{CAUSE_LABELS[proposed]}</span>
                </p>
              ) : (
                // Honesto, e não uma proposta vazia que se lê como defeito (FR-005).
                <Pill tone="info" withDot>
                  precisa abrir para julgar
                </Pill>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {proposed ? (
                <Button
                  size="sm"
                  disabled={isPending}
                  onClick={() =>
                    confirm.mutate({
                      appliedScenarioId: item.appliedScenarioId,
                      lineId: item.lineId,
                      cause: proposed,
                    })
                  }
                >
                  <Check className="h-4 w-4" aria-hidden />
                  {isPending ? "Registrando…" : "Confirmar"}
                </Button>
              ) : null}
              {/* Sempre disponível (FR-044): discordar da proposta leva ao compromisso, onde a
                  evidência inteira está — inclusive a interferência externa que a fila não leu. */}
              <Button size="sm" variant="outline" onClick={open}>
                {proposed ? "Discordar" : "Abrir"}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
